import {
    Fragment,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useInRouterContext } from 'react-router';
import { ChevronLeft, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { crumbHref } from '@/lib/crumb-return';
import { useParentCrumbShortcut } from '@/lib/use-parent-crumb-shortcut';

// The page toolbar: two bars at the top of each staff page (SEP-348).
//
// - The navigation bar (36px) is the same on each page. It holds the back and
//   forward buttons, the breadcrumb with the page title, and the global
//   buttons. It holds no control of the page.
// - The page bar (40px) is under it. It holds the controls of the page, in
//   the same order on each page: the tabs and the figures at the start, then
//   the filters, the search and the actions at the end. It shows only when the
//   page gives one of these. The bar wraps onto more lines when its items
//   do not fit, so it never hides a control.
//
// The layout owns the bars. It renders them for every page, so a page with no
// header still gets the breadcrumb and the global buttons. A page gives its
// title and its controls with `<PageToolbar>` (or `<PageHeader>`, which is the
// same component). Inside a host, `<PageToolbar>` renders nothing in place: it
// sends its title to the host and puts its controls into the slots of the page
// bar through portals. Outside a host (a test, or a page with no staff layout),
// it renders the bars in place.

/** A crumb that a page adds between its section and its title. */
export interface PageCrumb {
    label: string;
    to: string;
}

const EMPTY_TRAIL: readonly PageCrumb[] = [];

export interface PageToolbarProps {
    /** The last segment of the breadcrumb, and the h1 of the page. */
    title?: string;
    /** A tooltip on the title. It is not a visible line. */
    subtitle?: string;
    /** Tabs or a segmented control. They are first in the page bar. */
    tabs?: ReactNode;
    /** Filters at the end of the page bar, before the search. */
    filters?: ReactNode;
    /** A search box at the end of the page bar, before the actions. */
    search?: ReactNode;
    /** Buttons at the end of the page bar. */
    actions?: ReactNode;
    /**
     * Figures at the start of the page bar, after the tabs: a stat strip, a
     * row of count toggles, or the key facts of a record. The part wraps
     * when it does not fit.
     */
    filterBar?: ReactNode;
    /**
     * Crumbs between the section and the title, from the outermost to the
     * innermost. The toolbar adds the view and the section by itself.
     */
    trail?: readonly PageCrumb[];
    /**
     * Replaces the sidebar section crumb, for a page that the user reached
     * from another section. The trail comes after it.
     */
    section?: PageCrumb;
    /** Applies to the strip only when the component renders in place. */
    className?: string;
}

/** One segment of the breadcrumb before the title. */
export interface ToolbarCrumb {
    key: string;
    label: string;
    to: string;
}

/** What one `<PageToolbar>` gives to the host. */
export interface ToolbarEntry {
    title?: string;
    subtitle?: string;
    /** The page gives a control, so the page bar shows. */
    hasPageBar: boolean;
    trail: readonly PageCrumb[];
    /** Replaces the sidebar section crumb. */
    section?: PageCrumb;
}

interface StoredEntry extends ToolbarEntry {
    /** The mount order. The entry that mounted last owns the strip. */
    seq: number;
}

interface PageToolbarHostValue {
    upsert: (id: string, entry: ToolbarEntry) => void;
    remove: (id: string) => void;
    activeId: string | null;
    active: ToolbarEntry | null;
    startEl: HTMLElement | null;
    endEl: HTMLElement | null;
    setStartEl: (el: HTMLElement | null) => void;
    setEndEl: (el: HTMLElement | null) => void;
}

const PageToolbarHostContext = createContext<PageToolbarHostValue | null>(null);

// A page often gives a new array literal on each render. Compare the trail by
// its content, so that the host does not update in a loop.
function trailKey(trail: readonly PageCrumb[]): string {
    return trail.map((c) => `${c.label}\u0000${c.to}`).join('\u0001');
}

export function sameEntry(a: ToolbarEntry, b: ToolbarEntry): boolean {
    return (
        a.title === b.title &&
        a.subtitle === b.subtitle &&
        a.hasPageBar === b.hasPageBar &&
        trailKey(a.trail) === trailKey(b.trail) &&
        a.section?.label === b.section?.label &&
        a.section?.to === b.section?.to
    );
}

/** Keeps the entries of the page toolbars, and the slot elements of the page bar. */
export function PageToolbarProvider({ children }: { children: ReactNode }) {
    const [entries, setEntries] = useState<ReadonlyMap<string, StoredEntry>>(() => new Map());
    const [startEl, setStartEl] = useState<HTMLElement | null>(null);
    const [endEl, setEndEl] = useState<HTMLElement | null>(null);
    const seqRef = useRef(0);

    const upsert = useCallback((id: string, entry: ToolbarEntry) => {
        setEntries((prev) => {
            const old = prev.get(id);
            if (old && sameEntry(old, entry)) return prev;
            const next = new Map(prev);
            seqRef.current += old ? 0 : 1;
            next.set(id, { ...entry, seq: old?.seq ?? seqRef.current });
            return next;
        });
    }, []);

    const remove = useCallback((id: string) => {
        setEntries((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const value = useMemo<PageToolbarHostValue>(() => {
        let activeId: string | null = null;
        let active: StoredEntry | null = null;
        for (const [id, entry] of entries) {
            if (!active || entry.seq > active.seq) {
                activeId = id;
                active = entry;
            }
        }
        return {
            upsert,
            remove,
            activeId,
            active,
            startEl,
            endEl,
            setStartEl,
            setEndEl,
        };
    }, [entries, upsert, remove, startEl, endEl]);

    return (
        <PageToolbarHostContext.Provider value={value}>{children}</PageToolbarHostContext.Provider>
    );
}

/** The host side of the bars. It is null outside a `PageToolbarProvider`. */
export function usePageToolbarHost(): PageToolbarHostValue | null {
    return useContext(PageToolbarHostContext);
}

interface PageToolbarFrameProps {
    crumbs?: readonly ToolbarCrumb[];
    title: string;
    subtitle?: string;
    /** The start of the page bar: the tabs, then the figures. It wraps. */
    start?: ReactNode;
    /** The end of the page bar: the filters, the search, then the actions. */
    end?: ReactNode;
    /** The global buttons at the right end of the navigation bar. */
    trailing?: ReactNode;
    /** Controls at the left end of the navigation bar, before the breadcrumb. */
    leading?: ReactNode;
    /** Shows the page bar. The default is true when `start` or `end` is given. */
    showPageBar?: boolean;
    /** Slot refs for a host. The portals of the page fill these elements. */
    startRef?: (el: HTMLElement | null) => void;
    endRef?: (el: HTMLElement | null) => void;
    className?: string;
}

const CRUMB_LINK_CLASS =
    'rounded-[4px] underline-offset-2 hover:text-[rgb(var(--color-text-primary))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]';

/** A link that also works outside a router (a test renders the kit on its own). */
function CrumbLink({
    to,
    className,
    title,
    testId,
    children,
    onClick,
}: {
    to: string;
    className?: string;
    title?: string;
    testId?: string;
    children: ReactNode;
    onClick?: () => void;
}) {
    const inRouter = useInRouterContext();
    const href = crumbHref(to);
    if (!inRouter) {
        return (
            <a href={href} className={className} title={title} data-testid={testId}>
                {children}
            </a>
        );
    }
    return (
        <Link to={href} className={className} title={title} data-testid={testId} onClick={onClick}>
            {children}
        </Link>
    );
}

/** Registers the Alt+ArrowUp shortcut. It needs a router, so it is a child. */
function ParentShortcut({ to }: { to: string }) {
    useParentCrumbShortcut(to);
    return null;
}

function CrumbSeparator({ className }: { className?: string }) {
    return (
        <span
            aria-hidden="true"
            className={cn(
                'flex-none select-none px-0.5 text-[rgb(var(--color-text-tertiary))] opacity-70',
                className,
            )}
        >
            /
        </span>
    );
}

/** The `…` button. It opens a small menu with the crumbs that the strip hides. */
function CrumbOverflow({ crumbs }: { crumbs: readonly ToolbarCrumb[] }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLLIElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <li ref={rootRef} className="relative hidden items-center gap-1 lg:flex">
            <button
                ref={buttonRef}
                type="button"
                data-testid="breadcrumb-overflow"
                aria-label="Show hidden breadcrumbs"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                className={cn(
                    'grid h-5 w-5 place-items-center text-[rgb(var(--color-text-tertiary))]',
                    CRUMB_LINK_CLASS,
                )}
            >
                <MoreHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
            <CrumbSeparator />
            {open && (
                <ul className="absolute left-0 top-full z-50 mt-1 flex min-w-[10rem] max-w-[18rem] flex-col rounded-[6px] border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))] py-1 shadow-md">
                    {crumbs.map((crumb) => (
                        <li key={crumb.key}>
                            <CrumbLink
                                to={crumb.to}
                                testId={`breadcrumb-link-${crumb.key}`}
                                title={crumb.label}
                                onClick={() => setOpen(false)}
                                className="block truncate px-3 py-1 text-[13px] leading-5 text-[rgb(var(--color-text-secondary))] hover:bg-[rgb(var(--color-sidebar-hover)/0.06)] hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--color-accent)/0.28)]"
                            >
                                {crumb.label}
                            </CrumbLink>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

/**
 * The breadcrumb: each crumb links to its page, and the h1 is the last item.
 *
 * - The crumb just before the h1 is the parent. It has more weight, and
 *   Alt+ArrowUp goes to it.
 * - A trail with more than three links keeps the first and the parent. The
 *   other crumbs go into the `…` menu.
 * - Below `lg` only the parent shows, with a left chevron before it. From
 *   `lg` the parent has no chevron, because the trail already shows the way.
 *   A page at the root of its section has no parent there.
 */
function Breadcrumb({
    crumbs,
    title,
    subtitle,
}: {
    crumbs: readonly ToolbarCrumb[];
    title: string;
    subtitle?: string;
}) {
    const inRouter = useInRouterContext();
    // The view crumb alone is not a parent: it is the top of the tree.
    const parent = crumbs.length > 1 ? crumbs[crumbs.length - 1] : undefined;
    const collapse = crumbs.length > 3;
    const hidden = collapse ? crumbs.slice(1, -1) : [];

    return (
        <nav aria-label="Breadcrumb" className="min-w-0 shrink">
            {inRouter && parent && <ParentShortcut to={parent.to} />}
            <ol className="flex min-w-0 items-center gap-1 text-screen-title">
                {crumbs.map((crumb, index) => {
                    const isParent = parent !== undefined && crumb.key === parent.key;
                    if (collapse && index > 0 && !isParent) return null;
                    return (
                        <Fragment key={crumb.key}>
                            <li
                                className={cn(
                                    'min-w-0 items-center gap-1',
                                    // The mobile top bar names the view, so only
                                    // the parent shows below `lg`.
                                    isParent ? 'flex' : 'hidden lg:flex',
                                )}
                            >
                                <CrumbLink
                                    to={crumb.to}
                                    testId={`breadcrumb-link-${crumb.key}`}
                                    title={
                                        isParent ? `Up to ${crumb.label} (Alt+\u2191)` : crumb.label
                                    }
                                    className={cn(
                                        'flex min-w-0 items-center',
                                        isParent
                                            ? 'font-medium text-[rgb(var(--color-text-secondary))]'
                                            : 'font-medium text-[rgb(var(--color-text-tertiary))]',
                                        CRUMB_LINK_CLASS,
                                    )}
                                >
                                    {isParent && (
                                        <ChevronLeft
                                            aria-hidden="true"
                                            className="h-3 w-3 flex-none lg:hidden"
                                        />
                                    )}
                                    <span className="max-w-[14rem] truncate whitespace-nowrap">
                                        {crumb.label}
                                    </span>
                                </CrumbLink>
                                <CrumbSeparator
                                    className={isParent ? 'hidden lg:block' : undefined}
                                />
                            </li>
                            {index === 0 && hidden.length > 0 && <CrumbOverflow crumbs={hidden} />}
                        </Fragment>
                    );
                })}
                <li className="min-w-0">
                    <h1
                        title={subtitle}
                        aria-current="page"
                        className="truncate text-screen-title text-[rgb(var(--color-text-primary))]"
                    >
                        {title}
                    </h1>
                </li>
            </ol>
        </nav>
    );
}

/**
 * The two bars. The breadcrumb is the navigation up the tree; the sidebar is
 * the navigation across it. Below `lg` the bars wrap, and the breadcrumb shows
 * the parent crumb only, because the mobile top bar names the view.
 */
export function PageToolbarFrame({
    crumbs = [],
    title,
    subtitle,
    start,
    end,
    trailing,
    leading,
    showPageBar,
    startRef,
    endRef,
    className,
}: PageToolbarFrameProps) {
    const hasPageBar = showPageBar ?? Boolean(start || end);
    return (
        <header
            data-testid="page-toolbar"
            className={cn(
                'flex-none border-b border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))]',
                className,
            )}
        >
            <div className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1 lg:h-9 lg:flex-nowrap lg:py-0">
                {leading}
                <Breadcrumb crumbs={crumbs} title={title} subtitle={subtitle} />
                {trailing && (
                    <div className="ml-auto flex flex-none items-center gap-1">{trailing}</div>
                )}
            </div>
            <div
                data-testid="page-bar"
                className={cn(
                    // The bar wraps when the two parts do not fit side by
                    // side. The end part then goes onto a second line, so no
                    // control is hidden and no menu is cut off.
                    'min-h-10 flex-wrap items-center gap-x-3 gap-y-1 border-t border-[rgb(var(--color-divider))] px-3 py-1',
                    hasPageBar ? 'flex' : 'hidden',
                )}
            >
                {/* The start wraps when its items do not fit. A hidden scroll
                    cut off the last figure, and nothing told the user. Its
                    basis is its content width, so a wide start pushes the end
                    onto the next line and the two parts never overlap. */}
                <div
                    ref={startRef}
                    className="flex min-w-[16rem] flex-[1_1_auto] flex-wrap items-center gap-x-3 gap-y-1 empty:hidden"
                >
                    {start}
                </div>
                <div
                    ref={endRef}
                    className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2 empty:hidden"
                >
                    {end}
                </div>
            </div>
        </header>
    );
}

// `useLayoutEffect` warns in a server render, and the tests render the kit on
// the server. The effect is only for the browser.
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The page side of the strip. `PageHeader` is this component with the same
 * props. Give ONE per page.
 */
export default function PageToolbar({
    title,
    subtitle,
    tabs,
    filters,
    search,
    actions,
    filterBar,
    trail,
    section,
    className,
}: PageToolbarProps) {
    const host = usePageToolbarHost();
    const id = useId();
    const hasPageBar = Boolean(tabs || filterBar || filters || search || actions);
    const pageTrail = trail ?? EMPTY_TRAIL;
    const upsert = host?.upsert;
    const remove = host?.remove;

    // The layout effect runs before the paint, so the strip never shows the
    // section label first and the page title after it.
    useBrowserLayoutEffect(() => {
        upsert?.(id, { title, subtitle, hasPageBar, trail: pageTrail, section });
    }, [upsert, id, title, subtitle, hasPageBar, pageTrail, section]);
    useBrowserLayoutEffect(() => {
        if (!remove) return undefined;
        return () => remove(id);
    }, [remove, id]);

    const start =
        tabs || filterBar ? (
            <>
                {tabs}
                {filterBar}
            </>
        ) : null;
    const end =
        filters || search || actions ? (
            <>
                {filters}
                {search ? <div className="w-64 max-w-full">{search}</div> : null}
                {actions}
            </>
        ) : null;

    if (!host) {
        return (
            <PageToolbarFrame
                crumbs={[
                    ...(section ? [{ key: 'section', label: section.label, to: section.to }] : []),
                    ...pageTrail.map((crumb, index) => ({
                        key: `trail-${index}`,
                        label: crumb.label,
                        to: crumb.to,
                    })),
                ]}
                title={title ?? ''}
                subtitle={subtitle}
                start={start}
                end={end}
                className={className}
            />
        );
    }

    if (host.activeId !== id) return null;
    return (
        <>
            {start && host.startEl && createPortal(start, host.startEl)}
            {end && host.endEl && createPortal(end, host.endEl)}
        </>
    );
}

/**
 * The breadcrumb segments and the h1 of the navigation bar.
 *
 * - The view label always comes first.
 * - A page title that names its section replaces the section label.
 * - On a page below the root of its section (a client record, a case), the
 *   section label stays as a segment and the page title is the h1.
 * - A page with a trail keeps the section segment at the root of its section
 *   too, because a record still needs the link up.
 * - The trail crumbs of the page come after the section. A crumb that links to
 *   the section is dropped.
 * - A page with no title takes the section label as the h1.
 * - A section override (`sectionOverride`) replaces the sidebar section. It
 *   always stays as a segment, also at the root of the section, because it
 *   is the way back to the place that the user came from.
 */
export function toolbarHeading(args: {
    viewLabel: string;
    viewHref: string;
    sectionLabel: string | null;
    sectionHref: string | null;
    sectionIsRoot: boolean;
    pageTitle?: string;
    trail?: readonly PageCrumb[];
    sectionOverride?: PageCrumb;
}): { crumbs: ToolbarCrumb[]; title: string } {
    const { viewLabel, viewHref, sectionIsRoot, pageTitle, sectionOverride } = args;
    const sectionLabel = sectionOverride ? sectionOverride.label : args.sectionLabel;
    const sectionHref = sectionOverride ? sectionOverride.to : args.sectionHref;
    const trail = args.trail ?? EMPTY_TRAIL;
    const crumbs: ToolbarCrumb[] = [{ key: 'view', label: viewLabel, to: viewHref }];
    if (!pageTitle) return { crumbs, title: sectionLabel ?? viewLabel };
    const keepSection = Boolean(sectionOverride) || !sectionIsRoot || trail.length > 0;
    if (sectionLabel && sectionHref && keepSection && sectionLabel !== pageTitle) {
        crumbs.push({ key: 'section', label: sectionLabel, to: sectionHref });
    }
    trail.forEach((crumb, index) => {
        // A crumb that links to the section is a duplicate of the section crumb.
        if (sectionHref && crumb.to === sectionHref) return;
        crumbs.push({ key: `trail-${index}`, label: crumb.label, to: crumb.to });
    });
    return { crumbs, title: pageTitle };
}
